import { normalizeApartmentReviews } from '@/src/lib/api/endpoints/apartmentReviews';
import type {
  ListingAreaSet,
  ListingDetailV3Area,
  ListingDetailV3Data,
  ListingDetailV3Floorplan,
  ListingDetailV3FloorplanUnit,
  ListingDetailV3Media,
  ListingDetailV3PetPolicy,
  ListingDetailV3PhotoConfig,
  ListingDetailV3Range,
  ListingDetailV3School,
  ListingDetailV3SimilarBuilding,
  ListingDetailV3SimilarListing,
  ListingPricingStats,
  ListingUnitPriceDrop,
} from '@/src/lib/api/endpoints/listing';
import { findUnitCostSelector } from '@/src/lib/utils/costAndFees';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { SimilarListing } from '@/src/components/features/property/hooks/useSimilarListings';
import type { ModelSummary } from '@/src/components/ListingCard/types';
import type { Amount, CostsAndFees } from '@/src/types/costAndFees.type';
import type { Amenities, ListingMinMaxPriceBeds, PetPolicy, Photo } from '@/src/types/commonTypes.type';
import type { ListingDetails } from '@/src/types/listingDetails.type';
import { groupPetPoliciesByKind, policiesForPet } from '@/src/lib/utils/petKind';
import type {
  PricingComparisonAreaType,
  SurroundingAreaStats,
} from '@/src/types/listingDetails.type';
import { LISTING_PHOTO_CAP } from '@/src/lib/utils/listingTransforms';
import { normalizeTotalMonthlyPriceDisplayInfo } from '@/src/lib/utils/totalMonthlyPrice';
import { hasV3ListingTag } from '@/src/lib/utils/v3ListingTags';
import { latestInteractionDate, userItemTypesFromInteractions } from '@/src/lib/utils/v3UserInteractions';
import { normalizeV3Contact } from '@/src/lib/utils/v3Contact';
import { buildV3AmenitiesPreview } from '@/src/components/features/property/AmenitiesModule/amenitiesPreview';

export { hasV3ListingTag } from '@/src/lib/utils/v3ListingTags';

type HdpPdpType = 'unit' | 'building';

export type V3AdaptedListingExtras = {
  costsAndFees?: CostsAndFees;
  similarListings?: Array<SimilarListing>;
  lotIdEncoded?: string;
  hdpUrl?: string;
};

type PriceRange = {
  min?: number;
  max?: number;
};

type LegacyRoomForRent = NonNullable<PropertyDetailListing['details']> extends { roomForRent: infer RoomForRent }
  ? RoomForRent
  : unknown;

type TourProviderLike = {
  dateOnly?: boolean;
  providerName?: string;
  supportsCancel?: boolean;
};

type InstantTourProvidersLike = {
  inPersonProvider?: TourProviderLike | null;
  liveVideoProvider?: TourProviderLike | null;
  selfGuidedProvider?: TourProviderLike | null;
};

type AdaptOptions = {
  pdpType?: HdpPdpType;
  requestedMaloneLotIdEncoded?: string;
  selectedUnit?: string | null;
  includeEmbeddedCollections?: boolean;
  pricingStats?: ListingPricingStats | null;
  priceDrops?: Array<ListingUnitPriceDrop>;
};

type TourEntry = {
  label: string;
  url: string;
};

const EMPTY_STRING = '';

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function asNumber(value: unknown): number | undefined {
  if (isFiniteNumber(value)) return value;
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}

function numberOrZero(value: unknown): number {
  return asNumber(value) ?? 0;
}

function minKnownPrice(...prices: Array<number>): number {
  const knownPrices = prices.filter((price) => price > 0);
  return knownPrices.length > 0 ? Math.min(...knownPrices) : 0;
}

function maxKnownPrice(...prices: Array<number>): number {
  const knownPrices = prices.filter((price) => price > 0);
  return knownPrices.length > 0 ? Math.max(...knownPrices) : 0;
}

function compactPriceRange(range: ListingDetailV3Range | undefined | null): PriceRange | null {
  const min = asNumber(range?.min);
  const max = asNumber(range?.max);
  if (min === undefined && max === undefined) return null;
  return {
    min: min ?? max,
    max: max ?? min,
  };
}

function rangeFromAmount(amount: Amount | undefined): PriceRange | null {
  if (!amount || !('min' in amount)) return null;
  const min = asNumber(amount.min);
  const max = asNumber(amount.max);
  if (min === undefined && max === undefined) return null;
  return {
    min: min ?? max,
    max: max ?? min,
  };
}

function priceFromCosts(costs: CostsAndFees['costs'] | undefined, preferTotalMonthlyPrice: boolean): PriceRange | null {
  const monthly = rangeFromAmount(costs?.monthly?.amount);
  const baseRent = rangeFromAmount(costs?.baseRent?.amount);
  return preferTotalMonthlyPrice ? monthly ?? baseRent : baseRent ?? monthly;
}

function formatCurrency(value: number | undefined): string {
  if (value === undefined) return '';
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

function formatRange(range: PriceRange | null): string {
  if (!range?.min) return '';
  if (!range.max || range.max === range.min) return formatCurrency(range.min);
  return `${formatCurrency(range.min)} - ${formatCurrency(range.max)}`;
}

function compactRange(minValue: unknown, maxValue: unknown): { min: number; max: number } | null {
  const min = asNumber(minValue);
  const max = asNumber(maxValue);
  if (min === undefined && max === undefined) return null;
  return {
    min: min ?? max!,
    max: max ?? min!,
  };
}

function formatBedRange(range: { min: number; max: number }, compact = false): string {
  if (range.min === 0 && range.max === 0) return 'Studio';
  const suffix = compact ? 'bd' : range.max === 1 ? 'bed' : 'beds';
  if (range.min === range.max) return compact ? `${range.min} ${suffix}` : `${range.min} ${suffix}`;
  const minDisplay = range.min === 0 ? 'Studio' : String(range.min);
  return compact ? `${minDisplay}-${range.max} bd` : `${minDisplay} - ${range.max} ${suffix}`;
}

function formatBathRange(range: { min: number; max: number }, compact = false): string {
  if (range.min === 0 && range.max === 0) return '';
  const suffix = compact ? 'ba' : range.max === 1 ? 'bath' : 'baths';
  if (range.min === range.max) return `${range.min} ${suffix}`;
  return compact ? `${range.min}-${range.max} ba` : `${range.min} - ${range.max} ${suffix}`;
}

function formatSqftRange(range: { min: number; max: number }): string {
  if (range.min === 0 && range.max === 0) return '';
  if (range.min === range.max) return `${range.min.toLocaleString('en-US')} sq ft`;
  return `${range.min.toLocaleString('en-US')} - ${range.max.toLocaleString('en-US')} sq ft`;
}

function normalizeListingType(value: string | undefined, tags: Array<string>): string {
  return value?.toLowerCase() ?? (hasV3ListingTag(tags, 'rental') ? 'rental' : '');
}

function normalizePropertyType(value: string | undefined): string {
  const normalized = value?.toLowerCase() ?? '';
  switch (normalized) {
    case 'apartment':
      return 'large';
    case 'duplex':
      return 'divided';
    case 'single_family':
    case 'singlefamily':
      return 'house';
    default:
      return normalized;
  }
}

function normalizeRentalApplicationStatus(value: string | undefined): string {
  const normalized = value?.toLowerCase() ?? '';
  return normalized === 'accepting' || normalized === 'accepted' ? 'accepted' : normalized;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeTourProvider(value: unknown): TourProviderLike | null {
  return isRecord(value) ? (value as TourProviderLike) : null;
}

function normalizeInstantTourProviders(value: unknown): InstantTourProvidersLike | null {
  if (!isRecord(value)) return null;

  const inPersonProvider = normalizeTourProvider(value.inPersonProvider);
  const liveVideoProvider = normalizeTourProvider(value.liveVideoProvider);
  const selfGuidedProvider = normalizeTourProvider(value.selfGuidedProvider);

  if (!inPersonProvider && !liveVideoProvider && !selfGuidedProvider) return null;

  return {
    inPersonProvider,
    liveVideoProvider,
    selfGuidedProvider,
  };
}

function derivePriceRange(input: {
  unit?: ListingDetailV3FloorplanUnit;
  floorplan?: ListingDetailV3Floorplan;
  summary?: ListingDetailV3Data['summary'];
}): PriceRange | null {
  if (input.unit) return compactPriceRange(input.unit.price);

  const unitPrices = (input.floorplan?.units ?? []).map((unit) => compactPriceRange(unit.price));
  const low = minKnownPrice(...unitPrices.map((price) => numberOrZero(price?.min)));
  const high = maxKnownPrice(...unitPrices.map((price) => numberOrZero(price?.max)));

  return (
    compactPriceRange(input.floorplan?.price) ??
    (low > 0 ? { min: low, max: high || low } : null) ??
    compactPriceRange(input.summary?.price)
  );
}

function photoUrl(
  id: string | undefined,
  config: ListingDetailV3PhotoConfig | undefined,
  size: keyof NonNullable<ListingDetailV3PhotoConfig['formats']>,
): string | null {
  if (!id) return null;
  if (/^https?:\/\//.test(id)) return id;

  const baseUrl = config?.baseUrl ?? '';
  const format = config?.formats?.[size] ?? config?.formats?.medium ?? '';
  if (!baseUrl && !format) return id;
  return `${baseUrl}${id}${format}`;
}

function orderedPhotoIds(media: ListingDetailV3Media | undefined): Array<string> {
  const heroId = media?.photoHeroId;
  const ids = media?.photos ?? [];
  return Array.from(new Set([heroId, ...ids].filter((id): id is string => Boolean(id))));
}

function buildPhotos(
  media: ListingDetailV3Media | undefined,
  config: ListingDetailV3PhotoConfig | undefined,
  size: keyof NonNullable<ListingDetailV3PhotoConfig['formats']>,
): Array<Photo> {
  return orderedPhotoIds(media)
    .map((id) => photoUrl(id, config, size))
    .filter((url): url is string => Boolean(url))
    .map((url) => ({ url, caption: EMPTY_STRING }));
}

function buildMedPhotoUrls(
  media: ListingDetailV3Media | undefined,
  config: ListingDetailV3PhotoConfig | undefined,
): Array<string> {
  return buildPhotos(media, config, 'medium')
    .map((photo) => photo.url)
    .slice(0, LISTING_PHOTO_CAP);
}

function summaryToModelSummary(summary: ListingDetailV3Data['summary'] | undefined): ModelSummary {
  return {
    minBeds: asNumber(summary?.beds?.min),
    maxBeds: asNumber(summary?.beds?.max),
    minPrice: asNumber(summary?.price?.min),
    maxPrice: asNumber(summary?.price?.max),
    minBaths: asNumber(summary?.baths?.min),
    maxBaths: asNumber(summary?.baths?.max),
    minSqft: asNumber(summary?.sqft?.min),
    maxSqft: asNumber(summary?.sqft?.max),
  };
}

function compactModelSummary(summary: ModelSummary): ModelSummary {
  return Object.fromEntries(
    Object.entries(summary).filter(([, value]) => value !== undefined),
  ) as ModelSummary;
}

function minMaxFromSummary(summary: ModelSummary): ListingMinMaxPriceBeds | undefined {
  const beds = compactRange(summary.minBeds, summary.maxBeds ?? summary.minBeds);
  const baths = compactRange(summary.minBaths, summary.maxBaths ?? summary.minBaths);
  const sqft = compactRange(summary.minSqft, summary.maxSqft ?? summary.minSqft);
  const price = compactRange(summary.minPrice, summary.maxPrice ?? summary.minPrice);
  if (!beds && !baths && !sqft && !price) return undefined;

  const result: Partial<ListingMinMaxPriceBeds> = {
    DATA_MODEL: 'ListingMinMaxPriceBeds',
  };

  if (price) {
    result.minPrice = price.min;
    result.maxPrice = price.max;
    result.priceDisplay = formatRange(price);
    result.priceDisplayRange = formatRange(price);
  }
  if (beds) {
    result.minBeds = beds.min;
    result.maxBeds = beds.max;
    result.bedDisplay = formatBedRange(beds);
    result.bedDisplayAllAvailable = formatBedRange(beds);
    result.bedDisplayCompact = formatBedRange(beds, true);
    result.bedDisplaySemiCompact = formatBedRange(beds, true);
  }
  if (baths) {
    result.minBaths = baths.min;
    result.maxBaths = baths.max;
    result.bathDisplay = formatBathRange(baths);
    result.bathDisplayCompact = formatBathRange(baths, true);
    result.bathDisplaySemiCompact = formatBathRange(baths, true);
  }
  if (sqft) {
    result.minSqft = sqft.min;
    result.maxSqft = sqft.max;
    result.sqftDisplay = formatSqftRange(sqft);
  }

  return result as ListingMinMaxPriceBeds;
}

function pricingFromSummary(summary: ModelSummary): PropertyDetailListing['pricing'] {
  const beds = compactRange(summary.minBeds, summary.maxBeds ?? summary.minBeds);
  const baths = compactRange(summary.minBaths, summary.maxBaths ?? summary.minBaths);
  const sqft = compactRange(summary.minSqft, summary.maxSqft ?? summary.minSqft);
  const price = compactRange(summary.minPrice, summary.maxPrice ?? summary.minPrice);
  const summaryFields = {
    ...(beds ? { bedsLow: beds.min, bedsHigh: beds.max } : {}),
    ...(baths ? { bathsLow: baths.min, bathsHigh: baths.max } : {}),
    ...(sqft ? { sqFtLow: sqft.min, sqFtHigh: sqft.max } : {}),
    ...(price ? { priceLow: price.min, priceHigh: price.max } : {}),
  } as NonNullable<PropertyDetailListing['pricing']>['summary'];

  return {
    summary: summaryFields,
  };
}

type MappedArea = NonNullable<ListingAreaSet['specific']>;

function mapArea(area: ListingDetailV3Area | undefined): MappedArea | undefined {
  if (!area) return undefined;
  return {
    ...area,
    uriV2: area.uri,
  };
}

function mapAreas(areas: ListingDetailV3Data['areas'] | undefined): ListingAreaSet {
  const links = areas?.links?.map(mapArea).filter((area): area is MappedArea => Boolean(area));
  return {
    specific: mapArea(areas?.specific),
    breadcrumbs: areas?.breadcrumbs?.map(mapArea).filter((area): area is MappedArea => Boolean(area)),
    links,
    schools: areas?.schools,
    districts: links?.filter((link) => {
      const type = link.type?.toLowerCase() ?? '';
      const resourceId = link.resourceId?.toLowerCase() ?? '';
      return type.includes('district') || resourceId.includes('school-district');
    }),
  };
}

function parsePricingPercentage(value: string): number | undefined {
  const normalized = value.trim().replace(/%$/, '');
  if (!normalized) return undefined;
  const percentageValue = Number(normalized) / 100;
  return Number.isFinite(percentageValue) && percentageValue > -1 ? percentageValue : undefined;
}

function buildSurroundingAreaStats(
  links: ListingAreaSet['links'],
  pricingStats: ListingPricingStats | null | undefined,
  minPrice: number | undefined,
  maxPrice: number | undefined,
): SurroundingAreaStats {
  const result: SurroundingAreaStats = {
    neighborhood: null,
    city: null,
    county: null,
    zip: null,
  };
  const low = asNumber(minPrice);
  const high = asNumber(maxPrice ?? minPrice);
  if (!links?.length || !pricingStats || low === undefined || high === undefined) return result;

  const averagePrice = (low + high) / 2;
  if (averagePrice <= 0) return result;

  for (const area of links) {
    const areaType = area.type?.toLowerCase() as PricingComparisonAreaType | undefined;
    if (!areaType || !(areaType in result) || result[areaType]) continue;

    const id = area.id == null ? '' : String(area.id);
    const percentage = pricingStats[id];
    if (!percentage) continue;

    const percentageValue = parsePricingPercentage(percentage);
    const name = area.name?.trim();
    const link = area.uriV2 ?? area.uri;
    if (percentageValue === undefined || !name || !link) continue;

    const areaAveragePrice = Math.round(averagePrice / (percentageValue + 1));
    const priceDifference = Math.abs(Math.round(averagePrice - averagePrice / (percentageValue + 1)));
    result[areaType] = {
      id,
      name,
      type: areaType,
      link,
      listPrice: averagePrice,
      areaAveragePrice,
      averagePrice,
      difference: `$${priceDifference}`,
      percentageValue,
    };
  }

  return result;
}

const DEFAULT_SCHOOL_SEARCH_SLUG = 'apartments-for-rent';

function getSearchSlugFromListingPropertyType(propertyType: string): string {
  switch (propertyType) {
    case 'house':
    case 'townhouse':
      return 'houses-for-rent';
    case 'condo':
      return 'condos-for-rent';
    default:
      return DEFAULT_SCHOOL_SEARCH_SLUG;
  }
}

function mapSchools(schools: Array<ListingDetailV3School> | undefined, searchSlug: string): PropertyDetailListing['schools'] {
  return (schools ?? []).map((school) => ({
    ...school,
    id: String(school.areaId ?? school.resourceId ?? ''),
    name: school.name ?? '',
    resourceId: school.resourceId ?? '',
    type: school.type ?? '',
    isPrivate: Boolean(school.isPrivate),
    lowGrade: school.lowGrade ?? '',
    highGrade: school.highGrade ?? '',
    rating: numberOrZero(school.rating),
    geo: {
      lat: numberOrZero(school.geo?.lat),
      lon: numberOrZero(school.geo?.lon),
    },
    uriV2: school.resourceId ? `/${school.resourceId}/${searchSlug}` : '',
  }));
}

function buildPetPolicyMap(policies: Array<ListingDetailV3PetPolicy> | undefined): Amenities['petPolicies'] | undefined {
  if (!policies?.length) return undefined;

  const petPolicies = groupPetPoliciesByKind(
    policies.map((raw) => ({
      petType: raw.petType,
      policy: {
        allowed: raw.allowed ?? null,
        petSize: raw.petSize?.toLowerCase() ?? null,
        deposit: raw.deposit == null ? null : String(raw.deposit),
        oneTimeFee: raw.oneTimeFee == null ? null : String(raw.oneTimeFee),
        monthlyFee: raw.monthlyFee == null ? '' : String(raw.monthlyFee),
        maxNumberAllowed: raw.maxNumberAllowed == null ? '' : String(raw.maxNumberAllowed),
        maxWeightAllowed: raw.maxWeightAllowed == null ? null : String(raw.maxWeightAllowed),
        comment: raw.comment ?? '',
        restriction: raw.restriction ?? '',
      } satisfies PetPolicy,
    })),
  );
  return Object.keys(petPolicies).length > 0 ? petPolicies : undefined;
}

function includesAny(values: Array<string>, needles: Array<string>): boolean {
  const text = values.join(' ').toLowerCase();
  return needles.some((needle) => text.includes(needle));
}

function buildAmenities(detail: ListingDetailV3Data): Amenities {
  const buildingAmenities = detail.amenities?.building ?? [];
  const unitAmenities = detail.amenities?.unit ?? [];
  const allAmenities = [...buildingAmenities, ...unitAmenities];
  const petPolicies = buildPetPolicyMap(detail.petPolicies);
  const catAllowed = policiesForPet(petPolicies, 'cat').some((policy) => policy.allowed);
  const dogAllowed = policiesForPet(petPolicies, 'dog').some((policy) => policy.allowed);
  const petsAllowed = catAllowed && dogAllowed
    ? 'Cats and Dogs Allowed'
    : catAllowed
      ? 'Cats Allowed'
      : dogAllowed
        ? 'Dogs Allowed'
        : '';

  return {
    hasAvailableInSelectUnits: Boolean(detail.amenities?.someAvailableInSelectUnits),
    amenities: allAmenities,
    modelAmenities: unitAmenities,
    highlights: allAmenities.map((amenity) => ({
      id: amenity.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase(),
      display: amenity,
      types: [amenity],
    })),
    condensedAmenities: {
      pets: petsAllowed,
      laundry: includesAny(allAmenities, ['in unit', 'in-unit', 'washer', 'dryer'])
        ? 'In Unit'
        : includesAny(allAmenities, ['laundry'])
          ? 'Shared'
          : '',
      parking: includesAny(allAmenities, ['parking', 'garage', 'carport']),
      airConditioning: includesAny(allAmenities, ['air conditioning', 'a/c', 'cooling']),
      gym: includesAny(allAmenities, ['fitness', 'gym']),
      pool: includesAny(allAmenities, ['pool', 'swimming']),
      security: includesAny(allAmenities, ['gated', 'doorman', 'security', 'concierge']) ? 'Security' : '',
      dishwasher: includesAny(allAmenities, ['dishwasher']),
    },
    petPolicies: petPolicies as Amenities['petPolicies'],
  };
}

function buildFloorplanMedia(
  media: ListingDetailV3Media | undefined,
  config: ListingDetailV3PhotoConfig | undefined,
) {
  return {
    layouts: (media?.layouts ?? [])
      .map((id) => photoUrl(id, config, 'large'))
      .filter((url): url is string => Boolean(url))
      .map((url) => ({ url, caption: EMPTY_STRING })),
    photos: buildPhotos(media, config, 'medium'),
    largePhotos: buildPhotos(media, config, 'large'),
    thumbPhotos: buildPhotos(media, config, 'thumb'),
  };
}

function tourEntriesFromMedia(media: ListingDetailV3Media | undefined): Array<TourEntry> {
  return (media?.tours ?? [])
    .map((tour) => {
      if (!tour.url) return null;
      return {
        label: tour.label ?? '',
        url: tour.url,
      };
    })
    .filter((tour): tour is TourEntry => Boolean(tour));
}

function uniqueTourEntries(entries: Array<TourEntry>): Array<TourEntry> {
  const seenUrls = new Set<string>();

  return entries.filter((entry) => {
    if (seenUrls.has(entry.url)) return false;
    seenUrls.add(entry.url);
    return true;
  });
}

function buildFloorplans(
  detail: ListingDetailV3Data,
  priceDrops: Array<ListingUnitPriceDrop> = [],
): ListingDetails['floorplans'] {
  return (detail.floorplans ?? []).map((floorplan) => {
    const priceRange = derivePriceRange({ floorplan, summary: detail.summary });
    const media = buildFloorplanMedia(floorplan.media, detail.photoConfig);
    const units = (floorplan.units ?? []).map((unit) => {
      const unitPriceRange = derivePriceRange({ unit, floorplan });
      const unitMedia = buildFloorplanMedia(unit.media, detail.photoConfig);
      const priceDrop = priceDrops.find(
        (candidate) =>
          candidate.floorplanModelId === (floorplan.modelId ?? '') &&
          ((candidate.unitModelId && candidate.unitModelId === (unit.modelId ?? '')) ||
            (candidate.unitName && candidate.unitName === (unit.name ?? ''))),
      )?.amount;
      return {
        modelId: unit.modelId ?? '',
        name: unit.name ?? unit.modelId ?? '',
        availabilityDate: unit.availabilityDate ?? floorplan.availabilityDate ?? '',
        beds: numberOrZero(unit.beds ?? floorplan.beds),
        baths: numberOrZero(unit.baths ?? floorplan.baths),
        sqft: numberOrZero(unit.sqft ?? floorplan.sqft),
        low: numberOrZero(unitPriceRange?.min),
        high: numberOrZero(unitPriceRange?.max ?? unitPriceRange?.min),
        priceRangeDisplay: formatRange(unitPriceRange) ? [formatRange(unitPriceRange)] : [],
        isNewUnit: unit.isNewUnit,
        createdAgo: unit.createdAgo,
        priceDrop,
        ...unitMedia,
      };
    });

    return {
      modelId: floorplan.modelId ?? '',
      name: floorplan.name ?? '',
      type: '',
      availabilityDate: floorplan.availabilityDate ?? units[0]?.availabilityDate ?? '',
      beds: numberOrZero(floorplan.beds),
      baths: numberOrZero(floorplan.baths),
      sqft: numberOrZero(floorplan.sqft),
      low: numberOrZero(priceRange?.min),
      high: numberOrZero(priceRange?.max ?? priceRange?.min),
      priceRangeDisplay: formatRange(priceRange) ? [formatRange(priceRange)] : [],
      ...media,
      units,
      zillow3dTourUrl: floorplan.media?.tours?.[0]?.url ?? '',
    };
  });
}

export function applyV3CostsAndFees(
  listing: PropertyDetailListing,
  costsAndFees: CostsAndFees,
): PropertyDetailListing & V3AdaptedListingExtras {
  const preferTotalMonthlyPrice = listing.hasTotalMonthlyPrice === true;
  const sourceFloorplans = listing.floorplans as ListingDetails['floorplans'] | undefined;
  const floorplans = sourceFloorplans?.map((floorplan) => {
    const units = floorplan.units.map((unit) => {
      const selector = findUnitCostSelector(costsAndFees, unit.name, unit.modelId, unit.beds);
      const priceRange =
        unit.low > 0
          ? { min: unit.low, max: unit.high || unit.low }
          : priceFromCosts(selector?.selector.costs, preferTotalMonthlyPrice);
      if (!priceRange) return unit;
      const priceRangeDisplay = formatRange(priceRange);

      return {
        ...unit,
        low: numberOrZero(priceRange.min),
        high: numberOrZero(priceRange.max ?? priceRange.min),
        priceRangeDisplay: priceRangeDisplay ? [priceRangeDisplay] : [],
      };
    });
    const low = minKnownPrice(...units.map((unit) => unit.low));
    if (low === 0) return { ...floorplan, units };
    const high = maxKnownPrice(...units.map((unit) => unit.high));
    const priceRange = compactRange(low, high);
    const priceRangeDisplay = formatRange(priceRange);

    return {
      ...floorplan,
      low,
      high,
      priceRangeDisplay: priceRangeDisplay ? [priceRangeDisplay] : [],
      units,
    };
  });

  return {
    ...listing,
    costsAndFees,
    floorplans,
    modelsAndPricing: floorplans ? buildModelsAndPricing(floorplans) : listing.modelsAndPricing,
    faqModelsAndPricing: listing.faqModelsAndPricing?.map((model) => {
      const units = (floorplans ?? []).filter((floorplan) => floorplan.beds === model.numBeds).flatMap((floorplan) => floorplan.units);
      return {
        ...model,
        lowPrice: minKnownPrice(model.lowPrice, ...units.map((unit) => unit.low)),
        highPrice: maxKnownPrice(model.highPrice, ...units.map((unit) => unit.high)),
      };
    }),
  };
}

function buildModels(detail: ListingDetailV3Data): PropertyDetailListing['models'] {
  return (detail.floorplans ?? []).map((floorplan) => {
    const priceRange = derivePriceRange({ floorplan, summary: detail.summary });
    return {
      numBeds: numberOrZero(floorplan.beds),
      beds: numberOrZero(floorplan.beds),
      baths: numberOrZero(floorplan.baths),
      sqft: numberOrZero(floorplan.sqft),
      low: numberOrZero(priceRange?.min),
      high: numberOrZero(priceRange?.max ?? priceRange?.min),
      lowPrice: numberOrZero(priceRange?.min),
      highPrice: numberOrZero(priceRange?.max ?? priceRange?.min),
    };
  }) as PropertyDetailListing['models'];
}

function buildModelsAndPricing(floorplans: Array<{ beds?: number; low: number; high: number; units?: Array<unknown> }>): PropertyDetailListing['modelsAndPricing'] {
  const modelsByBeds = new Map<number, NonNullable<PropertyDetailListing['modelsAndPricing']>[number]>();

  floorplans.forEach((floorplan) => {
    const numBeds = numberOrZero(floorplan.beds);
    const lowPrice = minKnownPrice(floorplan.low);
    const highPrice = maxKnownPrice(floorplan.high);
    const quantity = floorplan.units?.length || 1;
    const current = modelsByBeds.get(numBeds);

    modelsByBeds.set(
      numBeds,
      current
        ? {
            numBeds,
            lowPrice: minKnownPrice(current.lowPrice, lowPrice),
            highPrice: maxKnownPrice(current.highPrice, highPrice),
            quantity: current.quantity + quantity,
          }
        : { numBeds, lowPrice, highPrice, quantity },
    );
  });

  return Array.from(modelsByBeds.values()).sort((a, b) => a.numBeds - b.numBeds);
}

function buildFaqModelsAndPricing(detail: ListingDetailV3Data): PropertyDetailListing['faqModelsAndPricing'] {
  return buildModelsAndPricing((detail.floorplans ?? []).map((floorplan) => {
    const price = derivePriceRange({ floorplan });
    return {
      beds: floorplan.beds,
      low: numberOrZero(price?.min),
      high: numberOrZero(price?.max ?? price?.min),
      units: floorplan.units,
    };
  }));
}

function buildingUrlFromCanonical(canonicalUrl: string | undefined): string {
  if (!canonicalUrl) return '';
  if (canonicalUrl.endsWith('/building')) return canonicalUrl;
  return canonicalUrl.replace(/\/pad(?=$|\?)/, '/building');
}

function unitSegmentFromName(unitName: string): string {
  return encodeURIComponent(unitName.trim().replace(/^#/, '').toLowerCase());
}

function unitPadUrlFromCanonical(canonicalUrl: string | undefined, unitName: string): string {
  if (!canonicalUrl) return '';

  const [path, query = ''] = canonicalUrl.split('?');
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return '';

  const lastSegment = segments[segments.length - 1];
  if (lastSegment === 'pad' || lastSegment === 'building') {
    segments.pop();
  }

  const unitSegment = unitName ? unitSegmentFromName(unitName) : '';
  const nextSegments = unitSegment ? [...segments, unitSegment, 'pad'] : [...segments, 'pad'];
  const unitPath = `/${nextSegments.join('/')}`;
  return query ? `${unitPath}?${query}` : unitPath;
}

function buildListingBase(detail: ListingDetailV3Data, options: AdaptOptions = {}): PropertyDetailListing & V3AdaptedListingExtras {
  const tags = detail.tags ?? [];
  const hasTotalMonthlyPrice = hasV3ListingTag(tags, 'totalMonthlyPrice');
  const resolved = detail.resolved;
  const details = detail.details;
  const location = detail.location;
  const media = detail.media;
  const modelSummary = compactModelSummary(summaryToModelSummary(detail.summary));
  const listingMinMaxPriceBeds = minMaxFromSummary(modelSummary);
  const photos = buildPhotos(media, detail.photoConfig, 'medium');
  const largePhotos = buildPhotos(media, detail.photoConfig, 'large');
  const thumbPhotos = buildPhotos(media, detail.photoConfig, 'thumb');
  const medPhotoUrls = buildMedPhotoUrls(media, detail.photoConfig);
  const lotIdEncoded = resolved?.lotIdEncoded ?? options.requestedMaloneLotIdEncoded ?? '';
  const canonicalUrl = resolved?.canonicalUrl ?? '';
  const buildingUrl = buildingUrlFromCanonical(canonicalUrl);
  const listingType = normalizeListingType(details?.listingType, tags);
  const propertyType = normalizePropertyType(details?.propertyType);
  const rentalApplicationStatus = normalizeRentalApplicationStatus(details?.rentalApplicationStatus);
  const floorplans = buildFloorplans(detail, options.priceDrops);
  const floorplanUnitCount = detail.floorplans?.reduce((count, floorplan) => count + (floorplan.units?.length ?? 0), 0) ?? 0;
  const numUnits = Math.max(details?.numUnits ?? 0, floorplanUnitCount);
  const isApartmentBldg =
    listingType === 'rental' &&
    (propertyType === 'large' || propertyType === 'garden' || propertyType === 'medium');
  const belongsToMultipleUnitBuilding = isApartmentBldg || numUnits > 1;
  const isUnit =
    options.pdpType !== 'building' && (Boolean(options.selectedUnit?.trim()) || !belongsToMultipleUnitBuilding);
  const title = details?.title ?? details?.name ?? '';
  const displayName = details?.name ?? title ?? location?.address?.street ?? '';
  const hasSpecialOffers = hasV3ListingTag(tags, 'specialOffers') || Boolean(details?.specialOffers?.length || details?.specialOffer);
  const floorplanTourEntries = (detail.floorplans ?? []).flatMap((floorplan) => tourEntriesFromMedia(floorplan.media));
  const listingTourEntries = tourEntriesFromMedia(detail.media);
  const tourEntries = uniqueTourEntries([...floorplanTourEntries, ...listingTourEntries]);
  const tourUrls = tourEntries.map((tour) => tour.url);
  const tourLabels = tourEntries.map((tour) => tour.label);
  const external3dTourUrls = listingTourEntries;
  const hasFloorplanTourUrls = floorplanTourEntries.length > 0;
  const hasAny3dTourUrls = tourEntries.length > 0;
  const listingVideos = detail.media?.videos?.map((video) => ({ url: video.url ?? '', label: video.label ?? null })) ?? [];
  const areas = mapAreas(detail.areas);
  const surroundingAreaStats = buildSurroundingAreaStats(
    areas.links,
    options.pricingStats,
    modelSummary.minPrice,
    modelSummary.maxPrice,
  );
  const showPricingStats = Object.values(surroundingAreaStats).some(Boolean);
  const history = detail.history;
  const listedBy = detail.listedBy;
  const contactPhone = listedBy?.contactPhone ?? '';
  const contactName = listedBy?.contactName ?? '';
  const companyName = listedBy?.companyName ?? '';

  return {
    active: resolved?.isActive ?? hasV3ListingTag(tags, 'active'),
    address: {
      street: location?.address?.street ?? '',
      city: location?.address?.city ?? '',
      state: location?.address?.state ?? '',
      zip: location?.address?.zip ?? '',
      neighborhood: detail.areas?.specific?.name ?? '',
      hideStreet: hasV3ListingTag(tags, 'streetHidden'),
    },
    aliasEncoded: resolved?.listingAlias ?? '',
    amenities: buildAmenities(detail),
    amenitiesPreview: buildV3AmenitiesPreview({
      building: detail.amenities?.building,
      unit: detail.amenities?.unit,
      petPolicies: detail.petPolicies,
    }),
    areas: {
      ...areas,
      surroundingAreaStats,
    },
    belongsToMultipleUnitBuilding,
    broker: {
      isBrokerExclusiveListing: hasV3ListingTag(tags, 'brokerExclusive'),
      companyName,
      companyPhoneNumber: contactPhone || null,
      contactName: contactName || null,
      agentLicenseType: (listedBy?.agentLicenseType as string | null | undefined) ?? null,
      isBrokerListing: hasV3ListingTag(tags, 'brokerListing'),
    },
    building: options.pdpType === 'building' || belongsToMultipleUnitBuilding,
    contact: normalizeV3Contact(detail.contact),
    created: numberOrZero(history?.created?.date),
    details: {
      ...details,
      allowWebsite: Boolean(listedBy?.webSite),
      availabilityDate: '',
      contactTimes: details?.contactTimes ?? '',
      customerId: '',
      demoted: hasV3ListingTag(tags, 'demoted'),
      disclaimer: detail.legal?.disclaimer ?? '',
      feesAndDeposits: [],
      fullDescription: details?.fullDescription ?? '',
      furnished: hasV3ListingTag(tags, 'furnished'),
      external3dTourUrls,
      hasExternal3dTourUrls: external3dTourUrls.length > 0 || hasV3ListingTag(tags, 'external3dTour'),
      has3dTour: hasAny3dTourUrls || hasV3ListingTag(tags, 'zillow3dTour'),
      hasZillow3dTourUrls: hasFloorplanTourUrls || hasV3ListingTag(tags, 'zillow3dTour'),
      instantTourProviders: normalizeInstantTourProviders(details?.instantTourProviders),
      isBrokerListing: hasV3ListingTag(tags, 'brokerListing'),
      isMls: hasV3ListingTag(tags, 'mls'),
      keywords: detail.areas?.specific?.keywords ?? '',
      leaseTerms: details?.leaseTerms ?? '',
      leaseTermsAndUtilitiesShareDescription: '',
      logoUrlCdn: '',
      metaKeywords: detail.areas?.specific?.keywords ?? '',
      mlsLogo: '',
      mlsName: '',
      numUnits,
      paymentPerInquiry: 0,
      propertyType,
      rental: hasV3ListingTag(tags, 'rental'),
      rentalApplicationStatus,
      roomForRent: detail.roomForRent as LegacyRoomForRent,
      seniorHousing: hasV3ListingTag(tags, 'seniorHousing'),
      studentHousing: hasV3ListingTag(tags, 'studentHousing'),
      title,
      tourLabels,
      tourUrls,
      trusted: hasV3ListingTag(tags, 'trusted'),
      webSite: listedBy?.webSite ?? '',
      webSiteIsPayPerClick: hasV3ListingTag(tags, 'webSiteIsPayPerClick'),
      webSiteOriginalDomain: listedBy?.buildingWebsite ?? '',
      zillowUrl: '',
    },
    displayName,
    title,
    featuredPropertyProductId: 0,
    featuredPropertyProductType: '',
    floorplans,
    propertyMap: detail.propertyMap,
    reviews: normalizeApartmentReviews(detail.reviews),
    forRentUnitsInBuilding: (detail.floorplans ?? []).flatMap((floorplan) =>
      (floorplan.units ?? []).map((unit) => unit.name ?? unit.modelId ?? '').filter(Boolean),
    ),
    geo: {
      lat: numberOrZero(location?.geo?.lat),
      lon: numberOrZero(location?.geo?.lon),
    },
    history: {
      created: history?.created ?? { date: 0, ago: '' },
      activated: history?.activated ?? { date: 0, ago: '' },
      lastUpdated: history?.updated ?? { date: 0, ago: '' },
      lastSynced: history?.lastSynced ?? { date: 0, ago: '' },
    },
    iconType: isApartmentBldg ? 'building' : 'house',
    isAllowedToRetain: hasV3ListingTag(tags, 'allowedToRetain'),
    isApartmentBldg,
    displayTags: detail.displayTags ?? [],
    isPaidAndNotDemoted: hasV3ListingTag(tags, 'paidAndNotDemoted'),
    isPaidMultifamily: hasV3ListingTag(tags, 'paidMultifamily'),
    isUnit,
    legalDisclaimer: detail.legal?.disclaimer ?? '',
    largePhotos,
    listedBy: {
      companyName,
      contactPhone,
      contactName,
      disclaimer: null,
      landlordPhotoUrl: null,
      memberPhone: null,
      memberSince: listedBy?.memberSince ?? { date: 0, ago: '' },
      mlsLogo: null,
      webSite: listedBy?.webSite ?? '',
      logoUrlCdn: null,
    },
    listingMinMaxPriceBeds,
    listingType,
    lotIdEncoded,
    maloneLotIdEncoded: lotIdEncoded,
    medPhotoUrls,
    models: buildModels(detail),
    modelsAndPricing: buildModelsAndPricing(floorplans),
    faqModelsAndPricing: buildFaqModelsAndPricing(detail),
    modelSummary,
    modelTypeUnitCount: detail.floorplans?.length ?? 0,
    newUnitCount: detail.floorplans?.reduce((count, floorplan) => count + (floorplan.units?.length ?? 0), 0) ?? 0,
    openHouses: [],
    photoCount: orderedPhotoIds(media).length,
    photos,
    popularity: {
      summary: {
        from: detail.popularity?.from ?? { date: 0, ago: '' },
        favorites: 0,
        viewed: detail.popularity?.viewed ?? 0,
        inquiries: detail.popularity?.inquiries ?? 0,
      },
    },
    previewPhoto: largePhotos[0] ?? photos[0] ?? { url: '', caption: '' },
    pricing: pricingFromSummary(modelSummary),
    priority: 0,
    propertyType,
    recencyTime: {
      displayDiff: history?.updated?.ago ?? '',
      secondsDiff: 0,
      minutesDiff: 0,
      hoursDiff: 0,
      daysDiff: 0,
      weeksDiff: 0,
      monthsDiff: 0,
    },
    rentalApplicationStatus,
    schools: mapSchools(detail.areas?.schools, getSearchSlugFromListingPropertyType(propertyType)),
    score: 0,
    searchKeyword: detail.areas?.specific?.keywords ?? '',
    showPricingStats,
    specialOfferLabel: details?.specialOffer ?? (hasSpecialOffers ? 'Special offer' : ''),
    specialOffers: details?.specialOffers?.map((offer) => ({
      description: offer.description ?? '',
      endDate: offer.endDate ?? 0,
    })) ?? (details?.specialOffer ? [{ description: details.specialOffer, endDate: 0 }] : []),
    thumbPhotos,
    trusted: hasV3ListingTag(tags, 'trusted'),
    unit: resolved?.unit ?? '',
    updated: numberOrZero(history?.updated?.date),
    uriBuilding: buildingUrl,
    uriMalone: canonicalUrl,
    urlMaloneUnit: resolved?.unit ?? '',
    uriV2: canonicalUrl,
    userItemDates: {
      viewed: { date: 0, ago: '' },
      inquiry: { date: 0, ago: '' },
      hidden: { date: 0, ago: '' },
      favorite: { date: 0, ago: '' },
    },
    userItemTypes: detail.userInteractions ?? [],
    userOnWaitlist: hasV3ListingTag(tags, 'waitlisted'),
    waitlisted: hasV3ListingTag(tags, 'waitlisted'),
    zestimate: 0,
    hasTotalMonthlyPrice,
    totalMonthlyPriceDisplayInfo: normalizeTotalMonthlyPriceDisplayInfo(detail.totalMonthlyPriceDisplayInfo) ?? {
      tooltipTexts: {},
    },
    militaryHousing: hasV3ListingTag(tags, 'militaryHousing'),
    incomeRestricted: hasV3ListingTag(tags, 'incomeRestricted'),
    seniorHousing: hasV3ListingTag(tags, 'seniorHousing'),
    studentHousing: hasV3ListingTag(tags, 'studentHousing'),
    hasSpecialOffers,
    priceDropCount: 0,
    isWaitlisted: hasV3ListingTag(tags, 'waitlisted'),
    hasVideos: hasV3ListingTag(tags, 'hasVideos') || listingVideos.length > 0,
    listingVideos,
    hasOpenHouse: false,
    hasExternal3dTourUrls: external3dTourUrls.length > 0 || hasV3ListingTag(tags, 'external3dTour'),
    hasZillow3dTourUrls: hasFloorplanTourUrls || hasV3ListingTag(tags, 'zillow3dTour'),
    similarListings: options.includeEmbeddedCollections === false ? undefined : adaptV3SimilarListings(detail),
    hdpUrl: canonicalUrl,
  } as unknown as PropertyDetailListing & V3AdaptedListingExtras;
}

export function adaptV3ListingDetail(
  detail: ListingDetailV3Data,
  options: AdaptOptions = {},
): PropertyDetailListing & V3AdaptedListingExtras {
  return buildListingBase(detail, options);
}

export function adaptV3ListingDetailUnits(
  detail: ListingDetailV3Data,
  options: AdaptOptions = {},
): Array<PropertyDetailListing & V3AdaptedListingExtras> {
  const baseListing = buildListingBase(detail, { ...options, includeEmbeddedCollections: false });
  const basePhotos = {
    photos: baseListing.photos,
    largePhotos: baseListing.largePhotos,
    thumbPhotos: baseListing.thumbPhotos,
    medPhotoUrls: baseListing.medPhotoUrls,
    previewPhoto: baseListing.previewPhoto,
    photoCount: baseListing.photoCount,
  };

  return (detail.floorplans ?? []).flatMap((floorplan) =>
    (floorplan.units ?? []).map((unit) => {
      const unitName = unit.name ?? unit.modelId ?? '';
      const unitHdpUrl = unitPadUrlFromCanonical(baseListing.uriMalone || baseListing.hdpUrl || baseListing.uriBuilding, unitName);
      const unitLotIdEncoded = baseListing.maloneLotIdEncoded ?? '';
      const availabilityDate = unit.availabilityDate ?? floorplan.availabilityDate ?? '';
      const priceRange = derivePriceRange({ unit, floorplan });
      const unitSummary = compactModelSummary({
        minBeds: asNumber(unit.beds ?? floorplan.beds),
        maxBeds: asNumber(unit.beds ?? floorplan.beds),
        minBaths: asNumber(unit.baths ?? floorplan.baths),
        maxBaths: asNumber(unit.baths ?? floorplan.baths),
        minSqft: asNumber(unit.sqft ?? floorplan.sqft),
        maxSqft: asNumber(unit.sqft ?? floorplan.sqft),
        minPrice: priceRange?.min,
        maxPrice: priceRange?.max,
      });
      const media = unit.media ?? floorplan.media;
      const unitPhotos = buildFloorplanMedia(media, detail.photoConfig);
      const medPhotoUrls = buildMedPhotoUrls(media, detail.photoConfig);
      const availablePhotoFields = unitPhotos.largePhotos.length
        ? {
            ...unitPhotos,
            medPhotoUrls,
            previewPhoto: unitPhotos.largePhotos[0],
            photoCount: orderedPhotoIds(media).length,
          }
        : basePhotos;
      const model = {
        numBeds: numberOrZero(unit.beds ?? floorplan.beds),
        beds: numberOrZero(unit.beds ?? floorplan.beds),
        baths: numberOrZero(unit.baths ?? floorplan.baths),
        sqft: numberOrZero(unit.sqft ?? floorplan.sqft),
        low: numberOrZero(priceRange?.min),
        high: numberOrZero(priceRange?.max ?? priceRange?.min),
        lowPrice: numberOrZero(priceRange?.min),
        highPrice: numberOrZero(priceRange?.max ?? priceRange?.min),
      };

      return {
        ...baseListing,
        ...availablePhotoFields,
        aliasEncoded: '',
        areas: undefined,
        building: false,
        details: {
          ...baseListing.details,
          availabilityDate,
        },
        displayName: unitName ? `${baseListing.displayName} #${unitName}` : baseListing.displayName,
        floorplans: [],
        faqModelsAndPricing: undefined,
        forRentUnitsInBuilding: [],
        hdpUrl: unitHdpUrl,
        isUnit: true,
        listingMinMaxPriceBeds: minMaxFromSummary(unitSummary),
        lotIdEncoded: unitLotIdEncoded,
        maloneLotIdEncoded: unitLotIdEncoded,
        modelSummary: unitSummary,
        models: [model] as PropertyDetailListing['models'],
        modelsAndPricing: [
          {
            numBeds: numberOrZero(unit.beds ?? floorplan.beds),
            lowPrice: numberOrZero(priceRange?.min),
            highPrice: numberOrZero(priceRange?.max ?? priceRange?.min),
            quantity: 1,
          },
        ],
        pricing: pricingFromSummary(unitSummary),
        schools: undefined,
        similarListings: undefined,
        title: unitName || baseListing.title,
        unit: unitName,
        availabilityDate,
        urlMaloneUnit: unitName,
        uriMalone: unitHdpUrl,
        uriV2: unitHdpUrl,
      } as PropertyDetailListing & V3AdaptedListingExtras;
    }),
  );
}

function adaptSimilarPhotoConfig(listing: ListingDetailV3SimilarListing): {
  config?: ListingDetailV3PhotoConfig;
  media?: ListingDetailV3Media;
} {
  const photos = listing.photos;
  if (!photos) return {};
  return {
    config: {
      baseUrl: photos.baseUrl,
      formats: photos.formats,
    },
    media: {
      photoHeroId: photos.heroId,
      photos: photos.photoIds,
    },
  };
}

function adaptSimilarListing(
  building: ListingDetailV3SimilarBuilding,
  listing: ListingDetailV3SimilarListing,
): SimilarListing {
  const { config, media } = adaptSimilarPhotoConfig(listing);
  const modelSummary = compactModelSummary(summaryToModelSummary(listing.summary));
  const photos = buildPhotos(media, config, 'medium');
  const medPhotoUrls = buildMedPhotoUrls(media, config);
  const tags = listing.tags ?? [];

  return {
    active: true,
    address: {
      street: listing.address?.street ?? '',
      city: listing.address?.city ?? '',
      state: listing.address?.state ?? '',
      hideStreet: hasV3ListingTag(tags, 'streetHidden'),
    },
    aliasEncoded: listing.listingAlias,
    amenities: {
      condensedAmenities: {},
    },
    displayName: listing.name ?? listing.title,
    geo: building.geo,
    listingMinMaxPriceBeds: minMaxFromSummary(modelSummary),
    listingType: normalizeListingType(listing.listingType, tags),
    lotIdEncoded: building.lotIdEncoded,
    maloneLotIdEncoded: building.lotIdEncoded,
    medPhotoUrl: medPhotoUrls[0],
    medPhotoUrls,
    modelSummary,
    name: listing.name,
    photoCount: orderedPhotoIds(media).length,
    photos,
    previewPhoto: photos[0],
    pricing: pricingFromSummary(modelSummary),
    propertyType: normalizePropertyType(listing.propertyType),
    rentalApplicationStatus: normalizeRentalApplicationStatus(listing.rentalApplicationStatus),
    searchKeyword: listing.searchKeyword,
    isDemoted: hasV3ListingTag(tags, 'demoted'),
    isPaidAndNotDemoted: hasV3ListingTag(tags, 'paidAndNotDemoted'),
    isPaidMultifamily: hasV3ListingTag(tags, 'paidMultifamily'),
    title: listing.title ?? listing.name,
    trusted: hasV3ListingTag(tags, 'trusted'),
    uriBuilding: building.uri,
    uriMalone: listing.uriMalone,
    uriV2: listing.uriMalone ?? building.uri,
    userItemTypes: userItemTypesFromInteractions(listing.userInteractions),
    userItemDates: { inquiry: { date: latestInteractionDate(listing.userInteractions, 'inquiry'), ago: '' } },
    isAllowedToRetain: hasV3ListingTag(tags, 'allowedToRetain'),
    hasTotalMonthlyPrice: hasV3ListingTag(tags, 'totalMonthlyPrice'),
    displayTags: listing.displayTags ?? [],
  };
}

export function adaptV3SimilarListings(detail: ListingDetailV3Data): Array<SimilarListing> {
  return (detail.similar ?? []).flatMap((building) =>
    (building.listings?.length ? building.listings : []).map((listing) =>
      adaptSimilarListing(building, listing),
    ),
  );
}

export function getEmbeddedV3Costs(
  listing: PropertyDetailListing | null | undefined,
): CostsAndFees | undefined {
  return (listing as (PropertyDetailListing & V3AdaptedListingExtras) | null | undefined)?.costsAndFees;
}

export function getEmbeddedV3SimilarListings(
  listing: PropertyDetailListing | null | undefined,
): Array<SimilarListing> | undefined {
  return (listing as (PropertyDetailListing & V3AdaptedListingExtras) | null | undefined)?.similarListings;
}
